import { storeNameForLocationId, storeRecordForLabel } from "./storeScope";

const STORAGE_KEY = "pg_selected_store";

function searchParams(search) {
  const raw = String(search || "");
  return new URLSearchParams(raw.startsWith("?") ? raw.slice(1) : raw);
}

function storeFromSearch(search) {
  const store = String(searchParams(search).get("store") || "").trim();
  if (/^\d+$/.test(store)) return { locationId: Number(store), storeName: "" };
  return { locationId: null, storeName: store };
}

export function readSelectedStore() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const locationId = Number(parsed?.locationId);
    if (!Number.isFinite(locationId)) return null;
    const brandRaw = parsed?.brandId;
    const brandId = brandRaw != null && brandRaw !== "" && Number.isFinite(Number(brandRaw))
      ? Number(brandRaw)
      : null;
    return { locationId, brandId };
  } catch {
    return null;
  }
}

export function writeSelectedStore(locationId, brandId) {
  const id = Number(locationId);
  if (!Number.isFinite(id)) return;
  const payload = { locationId: id };
  if (brandId != null && brandId !== "" && Number.isFinite(Number(brandId))) {
    payload.brandId = Number(brandId);
  }
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

/** Save the store the user just picked, before the page URL effect runs. */
export function rememberStoreByName(storeName, storeRecords) {
  const name = String(storeName || "").trim();
  if (!name || name === "All Stores") return;
  const rec = storeRecordForLabel(name, storeRecords);
  const id = Number(rec?.unified_location_id);
  if (!Number.isFinite(id)) return;
  const brandRaw = rec?.brand_id;
  const brandId = brandRaw != null && brandRaw !== "" && Number.isFinite(Number(brandRaw))
    ? Number(brandRaw)
    : null;
  writeSelectedStore(id, brandId);
}

function nameForLocation(locationId, brandId, storeRecords) {
  if (locationId == null || locationId === "") return "";
  const id = Number(locationId);
  const matches = (storeRecords || []).filter((row) => Number(row?.unified_location_id) === id);
  if (!matches.length) return storeNameForLocationId(locationId, storeRecords);
  if (brandId != null && brandId !== "") {
    const exact = matches.find((row) => Number(row.brand_id) === Number(brandId));
    if (exact?.location_name) return String(exact.location_name).trim();
  }
  const pg = matches.find((row) => Number(row.brand_id) === 15);
  const row = pg || matches[0];
  return row?.location_name ? String(row.location_name).trim() : "";
}

/** Remember a concrete store written into a page URL. "All Stores" does not clear it. */
export function rememberStoreFromSearch(search) {
  const { locationId } = storeFromSearch(search);
  if (locationId == null) return;
  const brandRaw = String(searchParams(search).get("brand") || "").trim();
  const existing = readSelectedStore();
  let brandId = null;
  if (/^\d+$/.test(brandRaw)) brandId = Number(brandRaw);
  else if (existing && existing.locationId === locationId) brandId = existing.brandId;
  writeSelectedStore(locationId, brandId);
}

function inCatalog(name, storeNames) {
  if (!name || name === "All Stores") return false;
  const names = storeNames || [];
  return !names.length || names.includes(name);
}

/**
 * Store name for this page: URL location, then the last store chosen on another page.
 * Empty until the catalog can resolve an id.
 */
export function resolveSharedStoreName({ search, storeRecords, storeNames } = {}) {
  const { locationId, storeName } = storeFromSearch(search);
  const remembered = readSelectedStore();
  const urlBrand = remembered && locationId != null && remembered.locationId === locationId
    ? remembered.brandId
    : null;
  const fromId = nameForLocation(locationId, urlBrand, storeRecords);
  if (inCatalog(fromId, storeNames)) return fromId;
  if (inCatalog(storeName, storeNames)) return storeName;
  const fromMemory = nameForLocation(remembered?.locationId, remembered?.brandId, storeRecords);
  if (inCatalog(fromMemory, storeNames)) return fromMemory;
  return "";
}

/** Keep the current query, and fill store (and ProcureIQ brand) from the last selection. */
export function searchCarryingStore(page, search) {
  const params = searchParams(search);
  const store = String(params.get("store") || "").trim();
  const remembered = readSelectedStore();
  if ((!store || store.toLowerCase() === "all") && remembered?.locationId != null) {
    params.set("store", String(remembered.locationId));
  }
  if (
    (page === "procureiq" || page === "procure-orders" || page === "procure-order") &&
    remembered?.brandId != null &&
    !String(params.get("brand") || "").trim()
  ) {
    const id = Number(params.get("store"));
    if (Number.isFinite(id) && remembered.locationId === id) {
      params.set("brand", String(remembered.brandId));
    }
  }
  const q = params.toString();
  return q ? `?${q}` : "";
}
